import { type FC, useCallback, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns/format'
import { isSameDay } from 'date-fns/isSameDay'
import { useUnit } from 'effector-react'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { CalendarDatePicker } from '@/components/ui/calendar/calendar-date-picker'
import { Dialog } from '@/components/ui/dialog'
import {
  FORM_ID,
  type FormValues,
  schema,
} from '@/dialogs/BirthdayEditDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { $fanBirthday } from '@/models/chattingUI/chats/chats.ts'
import { toDateFormat } from '@/modules/chatting/ui/utils.ts'

interface Props extends DialogProps {
  onEdit: (birthday: string) => Promise<unknown>
}

export const BirthdayEditDialog: FC<Props> = ({ isOpen, onClose, onEdit }) => {
  const birthday = useUnit($fanBirthday)
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      birthday: birthday ? new Date(toDateFormat(birthday)) : undefined,
    },
  })

  const selectedBirthday = form.watch('birthday')

  const isSameAsOriginal = useMemo(() => {
    if (!birthday || !selectedBirthday) return false

    return isSameDay(new Date(toDateFormat(birthday)), selectedBirthday)
  }, [birthday, selectedBirthday])

  const onSubmit = useCallback(
    async (data: FormValues) => {
      const birthday = format(data.birthday, 'yyyy-MM-dd')

      await onEdit(birthday)
      onClose()
    },
    [onClose, onEdit],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Edit Birthday</Dialog.Title>
        </Dialog.Header>
        <form
          id={FORM_ID}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <label className="text-title mb-1.5 block text-sm font-medium">
            Date
          </label>
          <Controller
            name="birthday"
            control={form.control}
            render={({ field }) => (
              <CalendarDatePicker
                date={field.value}
                onChange={field.onChange}
                className="w-full"
                yearAndMonthSelect
              />
            )}
          />
        </form>
      </div>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          form={FORM_ID}
          type="submit"
          isLoading={form.formState.isSubmitting}
          disabled={isSameAsOriginal || form.formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
